import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { dashboardAPI, profileAPI } from "../services/api";
import DashboardLayout from "../components/DashboardLayout";

export default function DashboardHome() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, isAuthenticated } = useSelector((state) => state.Auth);

    const [profileData, setProfileData] = useState({
        name: "",
        email: "",
        photo: null,
        bio: "",
        gender: "",
        languages: "",
        experience: "",
        specializations: [],
        certifications: [],
        services: [],
        availableDays: [],
        timeSlots: [],
        gallery: [],
        isProfileVisible: false,
        profileCompletionPercentage: 0,
        slug: "",
    });

    const [dashboardStats, setDashboardStats] = useState({
        total_clients: 0,
        total_leads: 0,
        total_ratings: 0,
        profile_views: 0,
    });

    const [loading, setLoading] = useState(true);

    // Fetch dashboard data from API
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const storedUser = localStorage.getItem("user");
                if (!storedUser && !isAuthenticated) {
                    navigate("/login");
                    return;
                }

                setLoading(true);
                const response = await dashboardAPI.getDashboard();

                if (response.data.success) {
                    // Update dashboard stats
                    setDashboardStats({
                        total_clients: response.data.total_clients || 0,
                        total_leads: response.data.total_leads || 0,
                        total_ratings: response.data.total_ratings || 0,
                        profile_views: response.data.profile_views || 0,
                    });

                    // Update profile data from API response
                    if (response.data.data) {
                        setProfileData((prev) => ({
                            ...prev,
                            ...response.data.data,
                        }));
                        // Also update localStorage with fresh data
                        localStorage.setItem('user', JSON.stringify(response.data.data));
                    }
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                toast.error('Failed to load dashboard data');

                // Fallback to localStorage if API fails
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                    const userData = JSON.parse(storedUser);
                    setProfileData((prev) => ({
                        ...prev,
                        ...userData,
                    }));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [navigate, isAuthenticated]);

    // Calculate profile completion
    const calculateProfileCompletion = () => {
        let completed = 0;
        const totalSteps = 6;

        if (profileData.photo) completed += 1;
        if (profileData.bio && profileData.gender && profileData.languages) completed += 1;
        if (profileData.experience && profileData.specializations.length > 0 && profileData.certifications.length > 0) completed += 1;
        if (profileData.services.length > 0) completed += 1;
        if (profileData.gallery.length >= 3) completed += 1;
        if (profileData.availableDays.length > 0 && profileData.timeSlots.length > 0) completed += 1;

        return Math.round((completed / totalSteps) * 100);
    };

    const handleVisibilityToggle = async () => {
        try {
            const newVisibility = !profileData.isProfileVisible;
            if (newVisibility && completionPercentage < 100) {
                toast.warning('Please complete your profile 100% before going live!');
                return;
            }

            const response = await profileAPI.toggleVisibility(newVisibility);

            if (response.data.success) {
                setProfileData(prev => ({
                    ...prev,
                    isProfileVisible: newVisibility
                }));
                toast.success(newVisibility ? 'Profile is now visible!' : 'Profile is now hidden');
            }
        } catch (error) {
            console.error('Error toggling visibility:', error);
            toast.error('Failed to update profile visibility');
        }
    };

    const getCompletionSteps = () => {
        return [
            {
                id: 1,
                title: "Add Profile Photo",
                description: "Upload a professional photo",
                completed: !!profileData.photo,
                icon: "bi-camera-fill",
                color: "#8b5cf6",
                action: () => navigate("/media"),
            },
            {
                id: 2,
                title: "Complete Basic Details",
                description: "Add bio, gender, languages",
                completed: !!(profileData.bio && profileData.gender && profileData.languages),
                icon: "bi-person-fill",
                color: "#3b82f6",
                action: () => navigate("/basic-info"),
            },
            {
                id: 3,
                title: "Add Professional Info",
                description: "Experience, specializations, certifications",
                completed: !!(
                    profileData.experience &&
                    profileData.specializations.length > 0 &&
                    profileData.certifications.length > 0
                ),
                icon: "bi-award-fill",
                color: "#f59e0b",
                action: () => navigate("/professional"),
            },
            {
                id: 4,
                title: "Add Services & Pricing",
                description: "Define your training services",
                completed: profileData.services.length > 0,
                icon: "bi-cash-stack",
                color: "#10b981",
                action: () => navigate("/services"),
            },
            {
                id: 5,
                title: "Upload Media",
                description: "Add gallery photos and videos",
                completed: profileData.gallery.length >= 3,
                icon: "bi-images",
                color: "#ec4899",
                action: () => navigate("/media"),
            },
            {
                id: 6,
                title: "Set Availability",
                description: "Choose your available days and times",
                completed: profileData.availableDays.length > 0 && profileData.timeSlots.length > 0,
                icon: "bi-calendar-check-fill",
                color: "#06b6d4",
                action: () => navigate("/availability"),
            },
        ];
    };

    const completionSteps = getCompletionSteps();
    const completedStepsCount = completionSteps.filter((step) => step.completed).length;
    const completionPercentage = calculateProfileCompletion();

    return (
        <DashboardLayout profileData={profileData} activeTab="dashboard">
            {/* Welcome Header with Gradient */}
            <div className="dashboard-welcome-header">
                <div className="row align-items-center">
                    <div className="col-md-8">
                        <h2 className="dashboard-page-title mb-1">
                            <span className="wave-emoji">👋</span> Welcome back, {profileData.name || 'Trainer'}!
                        </h2>
                        <p className="dashboard-page-subtitle">
                            Here's your profile status and performance overview
                        </p>
                    </div>
                    <div className="col-md-4 text-md-end">
                        <div className="d-flex align-items-center justify-content-end gap-3">
                            <div className="form-check form-switch custom-switch-lg">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="visibilitySwitch"
                                    checked={profileData.isProfileVisible}
                                    onChange={handleVisibilityToggle}
                                />
                                <label className="form-check-label visually-hidden" htmlFor="visibilitySwitch">
                                    Toggle Visibility
                                </label>
                            </div>
                            <div className="profile-status-badge-modern">
                                {profileData.isProfileVisible ? (
                                    <span className="status-visible-modern">
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        Profile Live
                                    </span>
                                ) : (
                                    <span className="status-hidden-modern">
                                        <i className="bi bi-x-circle-fill me-2"></i>
                                        Profile Hidden
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Cards with Gradient */}
            <div className="row g-3 mb-3">
                <div className="col-lg-3 col-md-6">
                    <div className="stat-card-modern stat-clients">
                        <div className="stat-card-content">
                            <div className="stat-icon-modern">
                                <i className="bi bi-people-fill"></i>
                            </div>
                            <div className="stat-details">
                                <h3 className="stat-number">{loading ? '...' : dashboardStats.total_clients}</h3>
                                <p className="stat-label">Total Clients</p>
                            </div>
                        </div>
                        <div className="stat-card-footer">
                            <i className="bi bi-arrow-up-right"></i>
                            <span>View all</span>
                        </div>
                    </div>
                </div>
                <div className="col-lg-3 col-md-6">
                    <div className="stat-card-modern stat-leads">
                        <div className="stat-card-content">
                            <div className="stat-icon-modern">
                                <i className="bi bi-person-plus-fill"></i>
                            </div>
                            <div className="stat-details">
                                <h3 className="stat-number">{loading ? '...' : dashboardStats.total_leads}</h3>
                                <p className="stat-label">New Leads</p>
                            </div>
                        </div>
                        <div className="stat-card-footer">
                            <i className="bi bi-arrow-up-right"></i>
                            <span>View all</span>
                        </div>
                    </div>
                </div>
                <div className="col-lg-3 col-md-6">
                    <div className="stat-card-modern stat-rating">
                        <div className="stat-card-content">
                            <div className="stat-icon-modern">
                                <i className="bi bi-star-fill"></i>
                            </div>
                            <div className="stat-details">
                                <h3 className="stat-number">{loading ? '...' : (dashboardStats.total_ratings || '0.0')}</h3>
                                <p className="stat-label">Average Rating</p>
                            </div>
                        </div>
                        <div className="stat-card-footer">
                            <i className="bi bi-arrow-up-right"></i>
                            <span>View reviews</span>
                        </div>
                    </div>
                </div>
                <div className="col-lg-3 col-md-6">
                    <div className="stat-card-modern stat-views">
                        <div className="stat-card-content">
                            <div className="stat-icon-modern">
                                <i className="bi bi-eye-fill"></i>
                            </div>
                            <div className="stat-details">
                                <h3 className="stat-number">{loading ? '...' : dashboardStats.profile_views}</h3>
                                <p className="stat-label">Profile Views</p>
                            </div>
                        </div>
                        <div className="stat-card-footer">
                            <i className="bi bi-arrow-up-right"></i>
                            <span>Analytics</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Completion Card - Modern Design */}
            <div className="profile-completion-card-modern">
                <div className="completion-header-modern">
                    <div className="completion-info">
                        <h4 className="completion-title">
                            <i className="bi bi-rocket-takeoff-fill me-2"></i>
                            Complete Your Profile
                        </h4>
                        <p className="completion-subtitle">
                            {profileData.isProfileVisible
                                ? "🎉 Your profile is live and visible to clients!"
                                : "Complete all steps to make your profile visible to potential clients"}
                        </p>
                    </div>
                    <div className="completion-circle-modern">
                        <svg width="140" height="140" className="progress-ring">
                            <defs>
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#cc0000" />
                                    <stop offset="100%" stopColor="#ff4444" />
                                </linearGradient>
                            </defs>
                            <circle
                                cx="70"
                                cy="70"
                                r="60"
                                fill="none"
                                stroke="#f0f0f0"
                                strokeWidth="12"
                            />
                            <circle
                                cx="70"
                                cy="70"
                                r="60"
                                fill="none"
                                stroke="url(#progressGradient)"
                                strokeWidth="12"
                                strokeDasharray={`${(completionPercentage / 100) * 377} 377`}
                                strokeDashoffset="0"
                                transform="rotate(-90 70 70)"
                                strokeLinecap="round"
                                className="progress-circle-animated"
                            />
                        </svg>
                        <div className="percentage-text-modern">
                            <span className="percentage-number">{completionPercentage}</span>
                            <span className="percentage-symbol">%</span>
                        </div>
                    </div>
                </div>

                <div className="completion-progress-modern">
                    <div className="progress-bar-modern">
                        <div
                            className="progress-fill-modern"
                            style={{ width: `${(completedStepsCount / completionSteps.length) * 100}%` }}
                        ></div>
                    </div>
                    <p className="steps-count-modern">
                        <strong>{completedStepsCount}</strong> of <strong>{completionSteps.length}</strong> steps completed
                    </p>
                </div>

                {/* Steps Grid */}
                <div className="steps-grid-modern">
                    {completionSteps.map((step, index) => (
                        <div
                            key={step.id}
                            className={`completion-step-modern ${step.completed ? "completed" : ""}`}
                            onClick={step.action}
                            style={{ animationDelay: `${index * 0.1}s` }}
                        >
                            <div className="step-icon-wrapper" style={{ backgroundColor: step.color + '15' }}>
                                <i className={step.icon} style={{ color: step.color }}></i>
                                {step.completed && (
                                    <div className="check-badge-modern" style={{ backgroundColor: step.color }}>
                                        <i className="bi bi-check-lg"></i>
                                    </div>
                                )}
                            </div>
                            <div className="step-content-modern">
                                <h6 className="step-title">{step.title}</h6>
                                <p className="step-description">{step.description}</p>
                            </div>
                            <div className="step-arrow-modern">
                                <i className="bi bi-arrow-right"></i>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Action Buttons */}
                {profileData.isProfileVisible && (
                    <div className="profile-actions-modern">
                        <button
                            className="btn btn-primary-modern btn-lg"
                            onClick={() => navigate(`/trainer/${profileData.slug || 'demo-trainer'}`)}
                        >
                            <i className="bi bi-eye-fill me-2"></i>
                            View My Public Profile
                        </button>
                        <button
                            className="btn btn-outline-modern btn-lg"
                            onClick={() => {
                                const profileUrl = `${window.location.origin}/trainer/${profileData.slug || 'demo-trainer'}`;
                                navigator.clipboard.writeText(profileUrl);
                                toast.success('Profile link copied to clipboard!');
                            }}
                        >
                            <i className="bi bi-share-fill me-2"></i>
                            Share Your Profile
                        </button>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
